import React, { useMemo } from 'react';
import TabSystem from '../../components/TabSystem';
import { Text } from '../../components/typography';
import styles from './Inspections.module.scss';
import { Button } from '../../components';
import InspectionsList from './InspectionsList';
import TemplatesList from './TemplatesList';
import { useQueryParams } from '../../hooks/misc';
import { searchParamKeys } from '../../constants/strings';
import { useModal } from '../../providers/ModalProvider';
import { modals } from '../../providers/modals';
import { useCurrentUser } from '../../providers/UserProvider';
import { useTranslation } from 'react-i18next';
import { keys } from '../../utilities/translator/translation_keys.js';
import { useWorkspace } from '../../providers/WorkspaceProvider';
import { loader } from 'graphql.macro';
import { gql } from '@apollo/client';
import { useApolloClient } from '@apollo/client';
import { fetchAllData } from '../../utilities/excel_export/table_excel_export.js';
import { useState } from 'react';
import { showToast } from '../../graphql/cache/modal.js';
import { toastVariant } from '../../constants/misc.js';
import { exportToExcel } from '../../components/excel/ExcelExport.jsx';
import {
  excelHeaderLookup,
  formatAssessment,
} from '../../utilities/excel_export/data_converter.js';
import format from 'date-fns/format';
import ProgressModal from '../../components/modals/ProgressModal.jsx';
import { useURLParams } from '../../providers/URLParamProvider.jsx';
import { useTemplates } from '../../providers/TemplateProvider.jsx';

const defaultTab = 'assessments';
const inspectionExportQuery = loader('./Inspections.fetch.graphql');

const typesToExclude = ['OBSERVATION', 'INCIDENT', 'AUDIT', 'QUALITY', 'MOC'];

export default function Inspections({ disabled = false }) {
  const { user, isAdmin, assessmentTypes } = useCurrentUser();
  const { getParam } = useQueryParams();
  const { openModal } = useModal();
  const tab = getParam(searchParamKeys.tab, defaultTab);
  const lockedType = getParam('lockedType');
  const lockedTypeObj = lockedType
    ? Object.values(assessmentTypes).find((t) => t.key === lockedType)
    : null;
  const { filters, subfilters } = useURLParams();
  const { t } = useTranslation();
  const client = useApolloClient();
  const [isFetchingAllData, setIsFetchingAllData] = useState(false);
  const [progress, setProgress] = useState(0);
  const { workspace, allWorkspaces, isWorkspaceAdmin } = useWorkspace();
  const { fetchTemplates } = useTemplates();

  const handleExport = async () => {
    const query = gql`
      ${inspectionExportQuery}
    `;
    await fetchAllData({
      query,
      client,
      setIsFetching: setIsFetchingAllData,
      table: 'inspections',
      count: 'inspectionsCount',
      filters,
      subfilters,
      setProgress,
    }).then((response, errors) => {
      if (errors?.length) {
        showToast({
          title: 'Error Exporting Data',
          variant: toastVariant.danger,
          message: `An error occurred: ${errors[0].message}`,
        });
      }

      if (response?.length) {
        const data = response.map((r) => {
          const workspaceData = allWorkspaces?.find(
            (w) => w.id === r?.workspaceId,
          );
          const reportedWorkspaceData = allWorkspaces?.find(
            (w) => w.id === r?.reportedWorkspaceId,
          );

          return {
            ...r,
            reportedWorkspace: reportedWorkspaceData,
            workspace: workspaceData,
          };
        });
        const flattenedData = data.flatMap((d) => {
          const reportedWorkspaceTitle = allWorkspaces?.find(
            (w) => w?.id === d?.workspaceId,
          )?.title;
          const formattedData = formatAssessment({
            ...d,
            reportedWorkspaceTitle,
          });
          const creatorId = d?.creator?.id;
          const filteredParticipants = (d.participants || []).filter(
            (participant) => participant.id !== creatorId,
          );
          const rowsWithParticipants = filteredParticipants?.map(
            (participant) => ({
              ...formattedData,
              participant: 'TRUE',
              creator: `${participant.firstName} ${participant.lastName}`,
              creatorId: `${participant.id}`,
              creatorEmail: participant.email,
              workspace: `${workspace?.title}`,
            }),
          );
          return [formattedData, ...rowsWithParticipants];
        });

        exportToExcel({
          data: flattenedData,
          sheetName: 'Assessment Data',
          exportedBy: `${user.firstName} ${user.lastName}`,
          type: 'Assessment',
          fileName: `Assessment_Export_${format(new Date(), 'yyyy-MM-dd')}`,
          reportType: ['Report Type', 'Assessment Table Export'],
          headerMap: excelHeaderLookup,
          workspace: `${workspace?.title}`,
        });
      }
      if (!response.length) {
        showToast({
          title: 'Error Exporting Data',
          variant: toastVariant.danger,
          message: `No Data to export.`,
        });
      }
    });
  };

  const allowedTypes = useMemo(() => {
    if (!isAdmin && !isWorkspaceAdmin) {
      const types = [];
      const roles = user?.workRoles || [];

      for (const role of roles) {
        const permissions = role.permissions?.inspectionTemplate ?? [];
        for (const permission of permissions) {
          types.push(permission.split('|')[1]);
        }
      }

      const flattenedTypes = Array.from(new Set(types));
      return Object.values(assessmentTypes)
        .map((type) => flattenedTypes.includes(type?.key) && type)
        .filter(({ key }) => !typesToExclude.includes(key))
        .filter(Boolean);
    } else {
      return Object.values(assessmentTypes)
        .map((type) => type)
        .filter(({ key }) => !typesToExclude.includes(key));
    }
  }, [isAdmin, isWorkspaceAdmin, user?.workRoles, assessmentTypes]);

  const canViewTable = useMemo(
    () => allowedTypes?.length > 0 || isAdmin || isWorkspaceAdmin,
    [allowedTypes, isAdmin, isWorkspaceAdmin],
  );

  const options = [
    {
      title:
        lockedTypeObj?.name ||
        user.company?.assessmentName ||
        t(keys.common.ASSESSMENTS),
      element: InspectionsList,
      key: 'assessments',
      params: { allWorkspaces: 'TRUE' },
    },
    ...(canViewTable
      ? [
          {
            title: t(keys.common.TEMPLATES),
            element: TemplatesList,
            key: 'templates',
            params: { allWorkspaces: 'TRUE' },
          },
        ]
      : []),
  ];
  return (
    <div className={disabled ? styles.disabled : null}>
      <div className={styles.headerContainer}>
        <Text size="lg" weight="bold" noMargin>
          {lockedTypeObj?.name ||
            user.company?.assessmentName ||
            t(keys.common.ASSESSMENTS)}
        </Text>
        {(canViewTable || tab === 'assessments') && (
          <div className={styles.buttons}>
            <Button
              value={t(keys.common.NEW)}
              variant="primary"
              icon="add"
              size="md"
              onClick={() => {
                fetchTemplates();
                switch (tab) {
                  case 'assessments':
                    openModal({
                      modalName: modals.createAssessment,
                      variables: {
                        participants: [user],
                        types: Object.values(assessmentTypes).filter(
                          (t) => !typesToExclude.includes(t.key),
                        ),
                        ...(lockedTypeObj ? { type: lockedTypeObj } : {}),
                      },
                    });
                    break;
                  case 'templates':
                    openModal({
                      modalName: modals.createTemplate,
                      variables: {
                        types: allowedTypes,
                        type: lockedTypeObj || allowedTypes[0],
                      },
                    });
                    break;
                  default:
                    throw new Error(`Invalid type ${tab}`);
                }
              }}
            />
            {tab === defaultTab && (isAdmin || isWorkspaceAdmin) && (
              <Button
                outlined
                value={t(keys.action.EXPORT)}
                onClick={handleExport}
                icon="file_upload"
              />
            )}
          </div>
        )}
      </div>
      <TabSystem type={tab} options={options} />
      <ProgressModal
        show={isFetchingAllData}
        progress={progress}
        title="Exporting Data"
        message="One moment while we fetch your data. Please don't close this window!"
        icon="import_export"
      />
    </div>
  );
}
